import React, { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Input,
  InputNumber,
  Button,
  Radio,
  Space,
  Table,
  TableColumnsType,
  Tabs,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_ADDRESS_LIFECYCLE,
  GET_ADDRESS_LIFECYCLE_DAILY_STATS,
  GET_ADDRESS_LIFECYCLE_RECENT,
  GetAddressLifecycleResponse,
  GetAddressLifecycleDailyStatsResponse,
  GetAddressLifecycleRecentResponse,
  LifecycleDailyStat,
  LifecycleEntry,
  LifecycleRecentEntry,
} from './graphql/addressLifecycle';
import {
  GET_STANDARDIZED_ADDRESS_LISTING,
  GetStandardizedAddressListingResponse,
  StandardizedAddressType,
} from '../standardized-address/graphql/queries/getStandardizedAddressListing';
import {
  changeTypeLabel,
  CHANGE_TYPE_COLORS,
  formatTime,
  tableLabel,
  formatValue,
  parseJsonSafe,
  pickField,
  sceneLabel,
} from './constants';

const { Text } = Typography;

/** 单条变更的 before→after 对照表（仅渲染 changed_fields 中的字段）。 */
const ChangeDiffTable: React.FC<{ entry: LifecycleEntry }> = ({ entry }) => {
  const before = parseJsonSafe(entry.before);
  const after = parseJsonSafe(entry.after);
  const rows = entry.changedFields.map((field) => ({
    key: field,
    field,
    before: formatValue(pickField(before, field)),
    after: formatValue(pickField(after, field)),
  }));
  if (rows.length === 0) return null;
  const columns: TableColumnsType<(typeof rows)[number]> = [
    { title: '字段', dataIndex: 'field', width: 180 },
    { title: '修改前', dataIndex: 'before', render: (v: string) => <Text type="secondary">{v}</Text> },
    { title: '修改后', dataIndex: 'after', render: (v: string) => <Text strong>{v}</Text> },
  ];
  return <Table size="small" pagination={false} columns={columns} dataSource={rows} />;
};

/** JSON 文本展示块，右上角按钮在 格式化/原始 之间切换（非法 JSON 时无按钮、原样展示）。 */
const JsonBlock: React.FC<{ text: string }> = ({ text }) => {
  const [pretty, setPretty] = useState(true);
  const prettyText = useMemo(() => {
    const parsed = parseJsonSafe(text);
    return parsed !== null ? JSON.stringify(parsed, null, 2) : null;
  }, [text]);
  return (
    <div>
      {prettyText !== null && (
        <div style={{ textAlign: 'right', marginBottom: 4 }}>
          <Button size="small" onClick={() => setPretty((v) => !v)}>
            {pretty ? '查看原始' : '格式化 JSON'}
          </Button>
        </div>
      )}
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
        {pretty && prettyText !== null ? prettyText : text}
      </pre>
    </div>
  );
};

/** 时间线单条内容。 */
const TimelineEntryCard: React.FC<{ entry: LifecycleEntry }> = ({ entry }) => {
  const isInsert = entry.changeType === 'INSERT' || entry.changeType === 'SNAPSHOT';
  const isBaseline = entry.changeScene === 'lazy_baseline';
  const collapseItems = [];
  if (entry.rawInput) {
    collapseItems.push({
      key: 'rawInput',
      label: '用户原始输入',
      children: <JsonBlock text={entry.rawInput} />,
    });
  }
  if (entry.providerRaw) {
    collapseItems.push({
      key: 'providerRaw',
      label: isBaseline
        ? `地址元数据备份（最近一次 API 返回${entry.provider ? `，来源 ${entry.provider}` : ''}）`
        : `外部API原始报文${entry.provider ? `（${entry.provider}）` : ''}`,
      children: <JsonBlock text={entry.providerRaw} />,
    });
  }
  if (isInsert && entry.after) {
    collapseItems.push({
      key: 'snapshot',
      label: isBaseline ? '当前态全量快照（查询时生成，非出生态）' : '标准化全量快照（落库时）',
      children: <JsonBlock text={entry.after} />,
    });
  }
  return (
    <Card size="small" style={{ marginBottom: 4 }}>
      <Space wrap size={[8, 4]}>
        <Tag color={CHANGE_TYPE_COLORS[entry.changeType] ?? 'default'}>{changeTypeLabel(entry.changeType)}</Tag>
        <Tag>{sceneLabel(entry.changeScene)}</Tag>
        {entry.changedTable && (
          <Tag color="geekblue">
            {tableLabel(entry.changedTable)}
            {entry.changedTable !== 'address_core' && ` #${entry.changedRowId}`}
          </Tag>
        )}
        <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(entry.changedAtMs)}</Text>
        {entry.provider && <Tag color="purple">{entry.provider}</Tag>}
        {entry.operator && <Text type="secondary">操作人: {entry.operator}</Text>}
        {entry.batchId && <Text type="secondary">批次: {entry.batchId}</Text>}
      </Space>
      {!isInsert && <div style={{ marginTop: 8 }}><ChangeDiffTable entry={entry} /></div>}
      {collapseItems.length > 0 && (
        <Collapse size="small" style={{ marginTop: 8 }} items={collapseItems} />
      )}
    </Card>
  );
};

/** Tab1: 单地址生命周期（搜索 + 摘要 + 时间线）。 */
const LifecycleTab: React.FC = () => {
  const [addressIdInput, setAddressIdInput] = useState<number | null>(null);
  const [line1, setLine1] = useState('');
  const [zipcode, setZipcode] = useState('');
  // false = 默认的最近变更列表; true = 单地址详情 (点行/查询后进入, 可回退)
  const [detailMode, setDetailMode] = useState(false);

  const [loadLifecycle, { data, loading, error: lifecycleError }] = useLazyQuery<GetAddressLifecycleResponse>(
    GET_ADDRESS_LIFECYCLE,
    { fetchPolicy: 'network-only' },
  );
  const [searchAddresses, { data: searchData, loading: searching }] =
    useLazyQuery<GetStandardizedAddressListingResponse>(GET_STANDARDIZED_ADDRESS_LISTING, {
      fetchPolicy: 'network-only',
    });
  // 默认视图: 全局最近变更 (近7天, 新→旧)
  const {
    data: recentData,
    loading: recentLoading,
    error: recentError,
    refetch: refetchRecent,
  } = useQuery<GetAddressLifecycleRecentResponse>(
    GET_ADDRESS_LIFECYCLE_RECENT,
    // notifyOnNetworkStatusChange: refetch 期间 loading 才会置 true, 刷新按钮/表格才有转圈动画
    { variables: { limit: 50 }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );

  const [currentId, setCurrentId] = useState<number | null>(null);

  const queryById = (id: number) => {
    setDetailMode(true);
    setCurrentId(id);
    loadLifecycle({ variables: { addressCoreId: id } });
  };

  const onSearchAddress = () => {
    if (!line1.trim() && !zipcode.trim()) {
      message.warning('请输入街道地址或邮编');
      return;
    }
    searchAddresses({
      variables: {
        filters: {
          line1: line1.trim() || undefined,
          zipcode: zipcode.trim() || undefined,
        },
        pageRequest: { page: 1, size: 20 },
      },
    });
  };

  const candidateColumns: TableColumnsType<StandardizedAddressType> = [
    { title: 'ID', dataIndex: 'id', width: 120 },
    { title: '标准地址', dataIndex: 'fullStandardAddress' },
    {
      title: '操作',
      width: 120,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => queryById(Number(row.id))}>
          查看生命周期
        </Button>
      ),
    },
  ];

  const view = data?.addressLifecycle;

  return (
    <div>
      <Card size="small" title="定位地址" style={{ marginBottom: 12 }}>
        <Space wrap>
          <InputNumber
            style={{ width: 220 }}
            placeholder="address_core_id 直查"
            value={addressIdInput}
            onChange={(v) => setAddressIdInput(v)}
            controls={false}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => (addressIdInput ? queryById(addressIdInput) : message.warning('请输入地址ID'))}
          >
            查询
          </Button>
          <Text type="secondary">或按地址搜索:</Text>
          <Input style={{ width: 260 }} placeholder="街道地址 (line1)" value={line1} onChange={(e) => setLine1(e.target.value)} />
          <Input style={{ width: 120 }} placeholder="邮编" value={zipcode} onChange={(e) => setZipcode(e.target.value)} />
          <Button icon={<SearchOutlined />} loading={searching} onClick={onSearchAddress}>
            搜地址
          </Button>
        </Space>
        {searchData && (
          <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey="id"
            columns={candidateColumns}
            dataSource={searchData.getStandardizedAddressListing.content}
            pagination={false}
          />
        )}
      </Card>

      {detailMode && (
        <Space style={{ marginBottom: 12 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              setDetailMode(false);
              refetchRecent(); // 回列表时拉最新, 刚发生的变更立刻可见
            }}
          >
            返回最近变更列表
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => currentId && queryById(currentId)}
          >
            刷新
          </Button>
        </Space>
      )}

      {detailMode && loading && <Card size="small" loading />}

      {detailMode && !loading && lifecycleError && (
        <Alert
          type="error"
          showIcon
          message="生命周期查询失败，请重试"
          description={lifecycleError.message}
        />
      )}

      {detailMode && view && !loading && (
        <>
          {!view.addressCore && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }} message="地址不存在（address_core 中未找到该 ID）" />
          )}
          {view.addressCore && (
            <Card size="small" title="当前状态（MySQL 实时）" style={{ marginBottom: 12 }}>
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="地址ID">{view.addressCore.id}</Descriptions.Item>
                <Descriptions.Item label="标准地址" span={2}>{view.addressCore.fullStandardAddress}</Descriptions.Item>
                <Descriptions.Item label="类型">{view.addressCore.addressType}</Descriptions.Item>
                <Descriptions.Item label="坐标">
                  {view.addressCore.latitude ?? '—'}, {view.addressCore.longitude ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="坐标来源">{view.addressCore.lnglatSource ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="变更次数">{view.totalChanges}</Descriptions.Item>
                <Descriptions.Item label="最早记录">{formatTime(view.firstChangeAtMs)}</Descriptions.Item>
                <Descriptions.Item label="最近变更">{formatTime(view.lastChangeAtMs)}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {!view.timelineAvailable && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="变更时间线数据源暂不可用，仅展示当前状态"
            />
          )}
          {view.entries.length > 0 && (
            <Card size="small" title={`变更时间线（旧 → 新，共 ${view.entries.length} 条）`}>
              <Timeline
                items={view.entries.map((entry) => ({
                  key: entry.changeId,
                  color: CHANGE_TYPE_COLORS[entry.changeType] ?? 'gray',
                  children: <TimelineEntryCard entry={entry} />,
                }))}
              />
            </Card>
          )}
        </>
      )}

      {!detailMode && (
        <Card
          size="small"
          title="最近变更"
          extra={
            <Button size="small" icon={<ReloadOutlined />} loading={recentLoading} onClick={() => refetchRecent()}>
              刷新
            </Button>
          }
        >
          {(recentData?.addressLifecycleRecent === null || recentError) && !recentLoading && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="时间线数据源暂不可用" />
          )}
          <Table<LifecycleRecentEntry>
            size="small"
            rowKey="changeId"
            loading={recentLoading}
            dataSource={recentData?.addressLifecycleRecent ?? []}
            pagination={false}
            onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => queryById(row.recordId) })}
            locale={{ emptyText: <Empty description="近 7 天暂无变更记录" /> }}
            columns={[
              { title: '时间', dataIndex: 'changedAtMs', width: 180, render: (v: number) => formatTime(v) },
              { title: '地址ID', dataIndex: 'recordId', width: 110 },
              {
                title: '标准地址', dataIndex: 'fullStandardAddress', width: 320, ellipsis: true,
                render: (v: string | null) => v ?? '—',
              },
              {
                title: '类型', dataIndex: 'changeType', width: 90,
                render: (v: string) => <Tag color={CHANGE_TYPE_COLORS[v] ?? 'default'}>{changeTypeLabel(v)}</Tag>,
              },
              {
                title: '变更表', dataIndex: 'changedTable', width: 110,
                render: (v: string, row: LifecycleRecentEntry) =>
                  `${tableLabel(v)}${v !== 'address_core' ? ` #${row.changedRowId}` : ''}`,
              },
              { title: '业务场景', dataIndex: 'changeScene', render: (v: string) => sceneLabel(v) },
              {
                title: '变更字段', dataIndex: 'changedFields',
                render: (fields: string[]) => (fields?.length ? fields.join(', ') : '—'),
              },
              { title: '操作人', dataIndex: 'operator', width: 110, render: (v: string | null) => v ?? '—' },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

/** Tab2: 变更量统计（近 N 天 × 来源 × 场景 × 类型）。 */
const StatsTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const { data, loading, error, refetch } = useQuery<GetAddressLifecycleDailyStatsResponse>(
    GET_ADDRESS_LIFECYCLE_DAILY_STATS,
    { variables: { days }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );

  const stats = data?.addressLifecycleDailyStats;

  const sceneTotals = useMemo(() => {
    const map = new Map<string, number>();
    // Long 标量运行时是字符串, 不 Number() 会变成字符串拼接 ("0500")
    (stats ?? []).forEach((s) => map.set(s.changeScene, (map.get(s.changeScene) ?? 0) + Number(s.count)));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [stats]);

  // 日期 → 0/1 奇偶: 相邻日期交替底色; 按日期算而非按行算, 用户改排序后颜色仍跟随日期
  const dateParity = useMemo(() => {
    const map = new Map<string, number>();
    Array.from(new Set((stats ?? []).map((s) => s.date)))
      .sort()
      .forEach((d, i) => map.set(d, i % 2));
    return map;
  }, [stats]);

  const columns: TableColumnsType<LifecycleDailyStat> = [
    { title: '日期', dataIndex: 'date', width: 120 },
    { title: '来源系统', dataIndex: 'sourceSystem', width: 130 },
    { title: '业务场景', dataIndex: 'changeScene', render: (v: string) => sceneLabel(v) },
    { title: '类型', dataIndex: 'changeType', width: 100, render: (v: string) => changeTypeLabel(v) },
    { title: '变更量', dataIndex: 'count', width: 120, sorter: (a, b) => Number(a.count) - Number(b.count) },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Radio.Group value={days} onChange={(e) => setDays(e.target.value)}>
          <Radio.Button value={7}>近 7 天</Radio.Button>
          <Radio.Button value={30}>近 30 天</Radio.Button>
          <Radio.Button value={93}>近 3 个月</Radio.Button>
        </Radio.Group>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refetch()}>
          刷新
        </Button>
      </Space>
      {(stats === null || error) && !loading && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="统计数据源暂不可用" />
      )}
      {sceneTotals.length > 0 && (
        <Card size="small" title="按场景汇总" style={{ marginBottom: 12 }}>
          <Space wrap>
            {sceneTotals.map(([scene, total]) => (
              <Tag key={scene}>{sceneLabel(scene)}: {total.toLocaleString()}</Tag>
            ))}
          </Space>
        </Card>
      )}
      <Table
        size="small"
        rowKey={(r) => `${r.date}-${r.sourceSystem}-${r.changeScene}-${r.changeType}`}
        loading={loading}
        columns={columns}
        dataSource={stats ?? []}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        rowClassName={(r) => (dateParity.get(r.date) === 1 ? 'bg-amber-50' : 'bg-blue-50')}
      />
    </div>
  );
};

const AddressLifecyclePage: React.FC = () => {
  return (
    <Card title="地址生命周期">
      <Tabs
        items={[
          { key: 'lifecycle', label: '生命周期查询', children: <LifecycleTab /> },
          { key: 'stats', label: '变更统计', children: <StatsTab /> },
        ]}
      />
    </Card>
  );
};

export default AddressLifecyclePage;
